// Response shapes from `api/src/internalTools/admin/master-dashboard/`.
// Hand-written rather than imported from a shared package — same isolation
// reasoning as the rest of this folder (see README).

import { RevenueBasis } from "./revenueBasis";

export type { RevenueBasis };

export type DateRangeKey = "today" | "7d" | "30d" | "90d" | "mtd";

export const DATE_RANGES: { key: DateRangeKey; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "7d", label: "7d" },
  { key: "30d", label: "30d" },
  { key: "90d", label: "90d" },
  { key: "mtd", label: "MTD" },
];

export const DEFAULT_RANGE: DateRangeKey = "30d";

export interface CustomerRow {
  uid: string;
  fullName: string;
  username: string;
  email: string;
  phoneNumber: string;
  profilePicURL: string;
  plan: string;
  subscriptionState: string;
  cancelAtEnd: boolean;
  subscriptionStart: string;
  subscriptionEnd: string;
  stripeAccountStatus: string;
  followerCount: number;
  /**
   * Joined server-side from the nightly rollup.
   *
   * `null` means the rollup has never written this customer — a genuine "not
   * measured yet", which the table shows as "—". A real 0 means they earned
   * nothing, and shows as $0.
   */
  lifetimeRevenueCents: number | null;
  /**
   * The same figure less Stripe's cut. Null on the same "not measured yet"
   * terms, plus one more: a rollup document written before Sep 2026 has no net
   * field at all, and printing "$0.00 net" beside a real gross figure would be
   * a worse lie than "—".
   */
  lifetimeNetRevenueCents: number | null;
  messagesSent30d: number | null;
  messagingSource: MessagingSource | null;
}

/**
 * Where a customer's messaging figures come from, and therefore whether there
 * is a figure at all.
 *
 * Two of these are real measurements and differ only in which Twilio API
 * answered:
 *
 * - `subaccount` — the creator owns a whole Twilio sub-account, measured with
 *   Usage Records. `spendCents` is Twilio's own `totalprice`: messages plus
 *   carrier fees and number rental.
 * - `dedicatedNumber` — the creator has their own Markit number that does not
 *   sit alone in a sub-account, measured by listing that number's messages.
 *   `messagesSent` is exact, and `spendCents` is **the same measurement as a
 *   `subaccount` figure** — that number's share of the real `totalprice` its
 *   account was billed, split by message segments. The two can be shown side by
 *   side, which they could not before Sep 2026, when this was message charges
 *   only and ran 34-52% of the comparable figure.
 *
 *   The UI still marks it, with a one-line note rather than a warning: the share
 *   is allocated rather than stated by Twilio, because carrier fees are not
 *   published per message and Conversations is billed per monthly active user.
 *   Measured against accounts where the true split is known, it lands within a
 *   median 6.2%.
 *
 * One is a genuine absence, and the UI must render "—" rather than a 0:
 *
 * - `sharedMainAccount` — no dedicated number at all (free tier / not yet
 *   approved), so the traffic goes out on Markit's shared number and Twilio
 *   pools it with no per-creator breakdown.
 *
 * And `unavailable` is the guard: a lookup failed, or one number is claimed by
 * more than one creator so any split would be invented.
 *
 * **`sharedMainAccount` used to mean far more than it does now, and it was
 * wrong.** Until Sep 2026 it meant "has no sub-account", which swept in 49
 * paying, approved creators who each have their own dedicated local number and
 * are perfectly measurable. The backend README has the finding.
 */
export type MessagingSource =
  | "subaccount"
  | "dedicatedNumber"
  | "sharedMainAccount"
  | "unavailable";

/** The two sources that carry a real measurement, and can therefore be ranked. */
export const isAttributedSource = (
  source: MessagingSource | null | undefined
): boolean => source === "subaccount" || source === "dedicatedNumber";

/** Freshness metadata every cached response carries. */
export interface SyncMeta {
  lastRunFinishedAt: string | null;
  status: "ok" | "partial" | "failed" | null;
  processedThroughDate?: string | null;
  errorCount?: number;
}

export interface CustomerListResponse {
  customers: CustomerRow[];
  limit: number;
  truncated: boolean;
  sync: { lastRunFinishedAt: string | null; status: string | null };
}

export interface CustomerDetail extends CustomerRow {
  subscription: {
    plan: string;
    baselineAmountCents: number;
    messageLimit: number | null;
    messagesUsed: number;
    periodStart: string;
    periodEnd: string;
    cancelAtEnd: boolean;
    downgradeScheduled: boolean;
    frozenDate: string;
  };
  stripeCustomerId: string;
  stripeAccountId: string;
  payoutSchedule: string;
  payoutsPaused: boolean;
  twilio: {
    subAccountSid: string;
    phone: string;
    phoneType: string;
    verificationStatus: string;
    verificationUpdatedAt: string;
    freeTierMessages: number;
  };
  profileReferralCount: number;
  eventCount: number;
}

export interface PlanBreakdownRow {
  plan: string;
  active: number;
  trial: number;
}

export interface OverviewResponse {
  range: { key: DateRangeKey; from: string; to: string; days: number };
  monetization: {
    payingCustomers: number;
    trials: number;
    trialsEndingSoon: number;
    trialEndingWindowDays: number;
    cancelAtEnd: number;
    paymentFailed: number;
    /**
     * Baseline plan list prices PLUS a projection of recurring overflow.
     *
     * **Overflow was excluded until Sep 2026, and that was not a small
     * omission**: measured across the 71 ACTIVE customers, trailing-30d
     * overflow was $12,481 against $3,417 of baseline subscription revenue —
     * **3.7x**. The old figure was reporting roughly a fifth of Markit's
     * recurring subscription revenue under a headline called "Estimated MRR".
     *
     * **This figure is NOT purely live and its card must not claim to be.** The
     * baseline half is read from Firestore on the request; the overflow half
     * comes from the nightly rollup's trailing windows, so the tile wears
     * `overflow.sync`'s clock. See the backend's `OVERFLOW_BLEND`.
     */
    estimatedMrrCents: number;
    /**
     * The same figure less Stripe's cut, or null when no rate could be
     * measured.
     *
     * **This is the one net figure on the dashboard that is a RATE rather than
     * an exact fee, and the card says so.** Every other net number subtracts a
     * fee Stripe actually charged on a charge that actually happened. Estimated
     * MRR is a projection of revenue that has not been billed yet, and a
     * payment Stripe has not processed has no fee to read — so its net form
     * applies Markit's realized subscription Stripe rate, given in full under
     * `stripeFee` so it is checkable.
     */
    estimatedNetMrrCents: number | null;
    stripeFee: {
      /**
       * The measured rate as a fraction. Null when no day in the window has a
       * recorded fee — the UI must then decline to show a net MRR rather than
       * show gross under a net label.
       */
      rate: number | null;
      /**
       * Both sides of the ratio, covering the SAME days. That is the property
       * that makes it meaningful while fee coverage is partial: an earlier
       * version divided a few days of fee by ninety days of revenue and
       * reported 0.10%, a plausible figure wrong by an order of magnitude.
       */
      revenueCents: number;
      feeCents: number;
      /** How much of the window the rate was actually measured over. */
      daysWithFee: number;
      daysInWindow: number;
      /**
       * Scoped to subscription revenue only — MRR contains no ticketing, and
       * ticketing carries a far heavier Stripe rate because Markit is merchant
       * of record on the whole ticket price.
       */
      basis: string;
    };
    /** The live half on its own — comparable to Stripe's own baseline-only MRR. */
    baselineMrrCents: number;
    overflow: {
      /**
       * The blended projection added to `estimatedMrrCents`:
       * `0.5 x trailing30d + 0.5 x (trailing90d / 3)`.
       *
       * Blended rather than plain trailing-30d because trailing-30d measured
       * WORST of the three candidates in a 12-month out-of-sample backtest —
       * median absolute error 19.9%, against 15.0% for this blend and 14.9%
       * for the 90-day mean. The blend is preferred over the 90-day mean
       * because that one lags a growing series badly (mean signed error
       * -13.6% vs -9.6%), and Markit's overflow nearly tripled in 14 months.
       */
      mrrCents: number;
      /** The two inputs, so the blend is checkable without re-deriving it. */
      trailing30dCents: number;
      trailing90dCents: number;
      weights: { trailing30d: number; trailing90dMonthly: number };
      customersWithOverflow: number;
      /**
       * ACTIVE customers the nightly rollup has no document for. Non-zero means
       * the overflow figure is an undercount by whatever they bill, and the UI
       * says so rather than showing a quietly short number.
       */
      customersWithoutStats: number;
      /** The overflow half's own clock — the rollup's, not this request's. */
      sync: { lastRunFinishedAt: string | null; status: string | null };
    };
    planBreakdown: PlanBreakdownRow[];
    scanLimit: number;
    scanTruncated: boolean;
  };
  planPricesCents: Record<string, number>;
}

export interface CustomerTextingResponse {
  massTextsSent: number | null;
  massTextsError: string | null;
}

// ---------------------------------------------------------------------------
// Nightly rollup (cached) + live Twilio. Hand-duplicated from the backend's
// response shapes, same as everything above — see this folder's README.
// ---------------------------------------------------------------------------

export interface PlatformDayStats {
  date: string;
  revenueCents: {
    /** Net of subscription refunds — do not subtract them again. */
    subscription: number;
    /** Net of subscription refunds — do not subtract them again. */
    overflow: number;
    ticketingFees: number;
    otherFees: number;
    /** Application fees refunded on ticket/product sales. Gross: subtract it. */
    refundedFees: number;
    /**
     * Subscription money Markit gave back. Reporting only — ALREADY netted out
     * of `subscription`/`overflow`. Absent on rows written before Sep 2026.
     */
    subscriptionRefunds?: number;
    /**
     * Stripe's own processing cut, split the same four ways as the revenue.
     *
     * **ABSENT on every row written before Sep 2026** — meaning "Stripe's cut
     * was never recorded for this day", not "Stripe took nothing". Treat
     * `undefined` as unknown and draw a gap on the net basis; a zero would
     * render as a real measurement that Markit paid no processing fees.
     */
    subscriptionStripeFee?: number;
    overflowStripeFee?: number;
    ticketingStripeFee?: number;
    otherStripeFee?: number;
    /**
     * Fee on succeeded charges that produced no recorded Markit revenue.
     * Recorded for auditability and netted off nothing — there is no revenue
     * line it belongs to. Measured at 0.5-1.1% of a day's total fees.
     */
    uncapturedStripeFee?: number;
  };
  /**
   * Null on a row written by the historical revenue-only backfill. MRR is
   * point-in-time, so stamping the current reading on a 2023 row would draw a
   * flat five-year line out of one measurement. Read MRR from `/overview`.
   */
  mrrCents: number | null;
  subscriptionsStarted: number;
  subscriptionsCanceled: number;
  /**
   * NULL means this day was never deliverability-scanned — a revenue-only
   * backfilled day. It is not `{delivered: 0, …}`, and the difference is the
   * whole point: a zero here would render as a real 0% delivery rate across
   * every backfilled day. Draw a gap, never a zero.
   */
  deliverability: {
    delivered: number;
    undelivered: number;
    failed: number;
    pending: number;
  } | null;
  syncedAt: string;
  syncRunId: string;
}

export interface PlatformStatsResponse {
  range: { key: DateRangeKey; from: string; to: string; days: number };
  days: PlatformDayStats[];
  /** Days in range the rollup hasn't written — rendered as gaps, never as zeros. */
  missingDays: string[];
  /**
   * Days that HAVE a revenue row but no deliverability scan. Distinct from
   * `missingDays`: revenue for these days is real and chartable, only the
   * delivery series has a hole. Absent on responses from before Sep 2026.
   */
  missingDeliverabilityDays?: string[];
  sync: SyncMeta;
}

/** One UTC day of message volume — platform-wide or for one customer. */
export interface VolumeDay {
  date: string;
  /** Outbound SMS + MMS. Inbound replies are not "messages sent". */
  messagesSent: number;
  spendCents: number;
}

/**
 * Cached — `adminMessagingStats/current`, rebuilt nightly from Twilio's own
 * Daily Usage Records at the main account.
 *
 * The WHOLE series comes down in one response (~1,710 days, 55.5 KB stored):
 * the day/week/month toggle regroups it client-side rather than refetching, and
 * the document is one Firestore read either way.
 *
 * Cached rather than live even though the range-total version of this call is
 * live on the same page — and that is a difference of clock, not of source.
 * Both ask Twilio. A range total is one call (~0.6s); a per-day history needs a
 * category-filtered call per series (~6.3s for 2022-2026), which is too slow for
 * a page load and only changes once a day.
 */
export interface PlatformVolumeResponse {
  /** Oldest-first. */
  days: VolumeDay[];
  /**
   * Twilio's own daily-record floor — measured at 2022-01-02. The SHORTEST of
   * this dashboard's three histories (Firebase Auth reaches 2020-12-17, Stripe
   * 2021-12-10), so the volume chart legitimately starts later than the revenue
   * one. Null before the first rebuild.
   */
  windowFrom: string | null;
  windowTo: string | null;
  totals: { messagesSent: number; spendCents: number; daysCovered: number } | null;
  computedAt: string | null;
  sync: SyncMeta;
}

/** One UTC day of Markit's own realized recurring revenue, in cents. */
export interface RevenueDay {
  date: string;
  /** Baseline subscription invoiced that day. Already net of refunds. */
  subscriptionCents: number;
  /** Metered per-message overflow invoiced that day. Already net of refunds. */
  overflowCents: number;
  /**
   * Stripe's own processing cut on each line, or `null` on a day nobody
   * measured it.
   *
   * **Null and 0 are different answers and the chart must treat them so.**
   * Every row written before Sep 2026 carries no fee at all, meaning "Stripe's
   * cut was never recorded for this day" — rendering that as 0 would draw five
   * years of net revenue sitting exactly on top of gross, a chart asserting
   * Markit paid no processing fees until September 2026. Those days are drawn
   * as a GAP on the net basis, the same treatment a day with revenue but no
   * deliverability already gets on the delivery chart.
   *
   * Not already subtracted from the two figures above — those stay exactly the
   * numbers this response has always carried, and the browser nets them only
   * when the reader asks for the net basis.
   */
  subscriptionFeeCents: number | null;
  overflowFeeCents: number | null;
}

/**
 * Cached — realized recurring revenue by day, for the whole of Stripe's history.
 *
 * **This is not, and must never be labeled as, "MRR over time".** Estimated MRR
 * is a forward projection of what recurs next month, and it exists for today
 * only: `AdminPlatformStats.mrrCents` is a point-in-time reading and is
 * deliberately absent from every backfilled row, because stamping one
 * measurement onto five years of days manufactures a flat line out of it.
 * Measured against the live collection: 4 of 1,743 rows carry `mrrCents`.
 *
 * What this carries instead is complete: what Markit actually invoiced each day,
 * split into baseline subscription and metered overflow, both present on
 * 1,743/1,743 rows back to 2021-12-01. Backward-looking realized revenue, not a
 * reconstruction of the projection — related measurements, different questions.
 *
 * The whole series comes down in one response (~96 KB) and the day/week/month
 * toggle regroups it client-side, exactly like `PlatformVolumeResponse`.
 */
export interface PlatformRevenueResponse {
  /** Oldest-first. */
  days: RevenueDay[];
  /** Oldest day with a rollup row — measured at 2021-12-01. Null before the first run. */
  windowFrom: string | null;
  windowTo: string | null;
  /**
   * Calendar days inside the window with no rollup row at all. Drawn as gaps,
   * never as zeros — a hole rendered as $0 reads as a day Markit earned nothing.
   * Measured at 0 today across the whole 1,743-day window.
   */
  missingDays: string[];
  /**
   * First day either line carries anything (2022-10-10 measured). Days before it
   * are REAL zeros, not absent data — every day from `windowFrom` has been
   * processed — so the chart draws them and the card says why.
   */
  recurringFrom: string | null;
  /** First day overflow carries anything (2024-06-18 measured) — it postdates baseline by years. */
  overflowFrom: string | null;
  /**
   * First day Stripe's cut was recorded, or null while no day has it.
   *
   * The net basis only exists from here forward. The card names this date
   * rather than drawing a net line that silently equals gross before it. It
   * stays near the present until the historical fee backfill runs — which is
   * deliberately after this ships and deploys, not before.
   */
  feeFrom: string | null;
  /** How many of `days` carry a fee — the net basis's own coverage. */
  daysWithFee: number;
  totals: {
    subscriptionCents: number;
    overflowCents: number;
    totalCents: number;
    /** Fee over the MEASURED days only — see `netCentsOverMeasuredDays`. */
    subscriptionFeeCents: number;
    overflowFeeCents: number;
    /**
     * Net over the measured days only, NOT over the whole window. Summing net
     * across days whose fee is unknown would silently count them as free.
     */
    netCentsOverMeasuredDays: number;
    daysCovered: number;
  } | null;
  sync: SyncMeta;
}

/**
 * Cached — one customer's day-level volume, read out of the `byDay` maps the
 * nightly rollup already writes inside their month documents.
 *
 * `messagingSource` is why a chart may legitimately be empty: a creator with no
 * dedicated Markit number of their own has no attributable volume at all and
 * must render "—" with the reason, never a zero.
 */
export interface CustomerVolumeResponse {
  days: VolumeDay[];
  messagingSource: MessagingSource | null;
  totals: {
    messagesSent: number;
    spendCents: number;
    messagesSent30d: number;
    messagesSent90d: number;
  } | null;
  sync: SyncMeta;
}

/**
 * The trailing windows the nightly rollup stores per customer.
 *
 * One type rather than two identical ones, because both leaderboards order off
 * the same three fields on the same `adminCustomerStats` documents — the volume
 * board on `…messagesSent`, the revenue board on `…totalRevenueCents`. The two
 * aliases below exist so a card imports a name that says what it ranks; they
 * are deliberately the same type, so `SegmentedToggle` options are shareable.
 *
 * Note the two boards default differently — volume to `30d`, revenue to
 * `lifetime` — which is a property of each endpoint, not of this type.
 */
export type StatsWindow = "30d" | "90d" | "lifetime";

export type VolumeWindow = StatsWindow;

export type RevenueWindow = StatsWindow;

export interface VolumeLeaderboardEntry {
  uid: string;
  fullName: string;
  username: string;
  profilePicURL: string;
  messagesSent: number;
  /**
   * The same measurement across every row: Twilio's real `totalprice` for the
   * account that creator's traffic was billed to — messages, carrier fees,
   * number rental and Conversations.
   *
   * A `subaccount` row owns its account outright and carries that figure whole.
   * A `dedicatedNumber` row shares an account, so it carries its share, split by
   * message segments; `messagingSource` still distinguishes the two because the
   * share is allocated rather than stated, but the two are comparable and the
   * board prints them in one column. Before Sep 2026 they were not: a
   * `dedicatedNumber` row was message charges only, 34-52% of the equivalent.
   */
  spendCents: number;
  lifetimeMessagesSent: number;
  messagingSource: MessagingSource | null;
}

export interface VolumeLeaderboardResponse {
  window: VolumeWindow;
  customers: VolumeLeaderboardEntry[];
  /**
   * Creators the board structurally cannot rank — they have no dedicated Markit
   * number, so their traffic goes out on the shared number and Twilio pools it.
   * Surfaced rather than dropped silently: "top senders" is a claim about the
   * platform, and this is the part of it Twilio cannot break down.
   */
  excluded: number;
  sync: SyncMeta;
}

/** Live from Twilio Usage Records at the main account — not cached. */
export interface MessagingResponse {
  range: { key: DateRangeKey; from: string; to: string; days: number };
  messagesSent: number;
  spendCents: number;
  accountSid: string;
  fetchedAt: string;
}

/**
 * Live — three Firestore collection-group counts, measured at ~400 documents in
 * ~760ms. Not cached, for the same reason Twilio volume isn't: caching a
 * sub-second query would only make it staler.
 */
export interface InstagramFunnelResponse {
  accountsConnected: number;
  /** Excludes Draft automations — a draft was never switched on. */
  automationsCreated: number;
  /** Sum of `numTriggered` across triggers — fires, not trigger documents. */
  automationsTriggered: number;
  /**
   * Distinct creators at each stage. The funnel chart uses THESE, not the three
   * totals above: those count accounts, automations and fires — different
   * things that grow rather than narrow, so charting them as funnel steps gave
   * step conversions of 465% and 2042%.
   */
  creators: {
    withAccounts: number;
    withAutomations: number;
    withTriggeredAutomations: number;
  };
  accountsByStatus: Record<string, number>;
  automationsByState: Record<string, number>;
  triggerDocuments: number;
  triggersNeverFired: number;
  fetchedAt: string;
}

/** One creator's Instagram connection, from `/engagement/instagram/accounts`. */
export interface InstagramAccountRow {
  creatorUid: string;
  creatorName: string;
  creatorUsername: string;
  /** The Instagram handle and id — not the creator's Markit username. */
  instagramUsername: string;
  instagramAccountId: string;
  profilePicUrl: string;
  status: string;
  connectedOn: string;
  /**
   * Automation figures belong to the CREATOR, not to this Instagram account —
   * `dmAutomations` lives under `users/{uid}/`, not under the account. A
   * creator with two connected accounts shows the same figures on both rows,
   * so these must never be summed across rows. `creatorAccountCount > 1` flags
   * exactly that case.
   */
  automationsTotal: number;
  /** Excludes Draft automations, matching the funnel's definition. */
  automationsLive: number;
  triggerDocuments: number;
  /** Sum of `numTriggered` — fires, not trigger documents. */
  automationFires: number;
  /** >1 when this creator has connected more than one Instagram account. */
  creatorAccountCount: number;
}

export interface InstagramAccountsResponse {
  accounts: InstagramAccountRow[];
  /** All counted over DISTINCT creators, never summed across account rows. */
  totals: {
    accounts: number;
    creators: number;
    automations: number;
    triggerDocuments: number;
    fires: number;
    creatorsWithoutLiveAutomation: number;
    creatorsNeverFired: number;
  };
  fetchedAt: string;
}

/** One referee under a referrer, from `/referrals`. */
export interface RefereeRow {
  uid: string;
  fullName: string;
  username: string;
  subscriptionState: string | null;
  totalMessagesAllTime: number;
  /** 10,000 on a Business plan, 2,500 otherwise. */
  requirement: number;
  requirementMet: boolean;
  /** True when Firestore has no user document for this uid at all. */
  missing: boolean;
}

export interface ReferrerRow {
  uid: string;
  fullName: string;
  username: string;
  profilePicURL: string;
  subscriptionState: string | null;
  lastReferralAt: string;
  referralCount: number;
  fulfilledCount: number;
  referees: RefereeRow[];
}

export interface ReferralsResponse {
  referrers: ReferrerRow[];
  totals: {
    referrers: number;
    referrals: number;
    distinctReferees: number;
    fulfilled: number;
    missingReferees: number;
  };
  requirements: { business: number; creator: number };
  fetchedAt: string;
}

/**
 * One event in the browsable list, from `/events`.
 *
 * `attendees` is the SAME figure the Home tab's "products with
 * submissions/tickets" aggregate counts — both come out of one pass of the
 * nightly scan over one snapshot, so the list and the aggregate cannot
 * disagree.
 */
export interface EventRow {
  id: string;
  title: string;
  start: string;
  end: string;
  timezone: string;
  eventType: string;
  createdBy: string;
  creatorUsername: string;
  creatorName: string;
  attendees: number;
  ticketsTotal: number;
}

export interface EventsResponse {
  events: EventRow[];
  /** Oldest day the rollup's window covers. Null before the first run. */
  windowFrom: string | null;
  eventsScanned: number;
  ticketsScanned: number;
  /** True when the stored row cap clipped the list. */
  truncated: boolean;
  attendeeThreshold: number;
  computedAt: string | null;
  sync: SyncMeta;
}

/**
 * One UTC day of a plain count series — Instagram connections, automations
 * created, products created.
 *
 * Deliberately `value` rather than a named field: these all go through the same
 * `CountChartCard` and the same bucketing, and a per-metric field name would
 * mean a per-metric chart component. `VolumeDay` stays its own shape because it
 * carries two correlated figures (messages and spend) that the volume chart
 * treats differently.
 */
export interface CountDay {
  date: string;
  value: number;
}

/**
 * Cached — `adminInstagramStats/current`.
 *
 * **Three of these four series are real history and the fourth is not, which is
 * the single most important thing about this response.** Verified against
 * production documents before the storage was designed:
 * `instagramAccounts.connectedOn` (37/37), `dmAutomations.createdAt` (177/177)
 * and `dmAutomationTriggers.createdAt` (186/186) are all populated ISO strings,
 * so those timelines were reconstructable with no new tracking at all and reach
 * back to the feature's own launch. Trigger FIRES carry no timestamp anywhere —
 * `numTriggered` is a lifetime counter incremented by `FieldValue.increment(1)`
 * with no per-fire record and no subcollection — so `firesByDay` can only ever
 * start the day the nightly snapshot began, and the UI must say so.
 */
export interface InstagramHistoryResponse {
  /** Full history. Oldest-first. */
  accountsConnectedByDay: CountDay[];
  /** Full history, non-Draft only — matching the funnel's `automationsCreated`. */
  automationsCreatedByDay: CountDay[];
  /** Full history. Triggers CREATED, not fired. */
  triggersCreatedByDay: CountDay[];
  /**
   * FORWARD-ONLY. Consecutive nightly snapshots of the cumulative fire counter,
   * differenced. Empty until two nights have been snapshotted.
   */
  firesByDay: CountDay[];
  /** First day ever snapshotted. Everything before it is unknown, permanently. */
  firesTrackedFrom: string | null;
  fireSnapshotDays: number;
  /** Days no snapshot covers — drawn as gaps, never as zero fires. */
  gapDays: string[];
  /**
   * Days the cumulative counter went DOWN — a deleted trigger document takes
   * its lifetime fires with it. Not a negative day; also a gap.
   */
  counterDecreaseDays: string[];
  totals: {
    accounts: number;
    automations: number;
    automationsNonDraft: number;
    triggerDocuments: number;
    /** Lifetime fires — the counter, not the sum of `firesByDay`. */
    fires: number;
  } | null;
  computedAt: string | null;
  sync: SyncMeta;
}

/** One creator on the "Top creators by products created" board. */
export interface EventCreatorRow {
  uid: string;
  username: string;
  name: string;
  /** Products created inside the window — the figure the board ranks on. */
  created: number;
  /**
   * Attendees across only this creator's SCORED products. Paired with
   * `scoredProducts` so the UI can show what share of their output it covers
   * rather than implying it covers all of it.
   */
  attendeesScored: number;
  scoredProducts: number;
  firstCreatedAt: string;
  lastCreatedAt: string;
  /** Sparse per-day creation counts — powers the row sparkline. */
  byDay: Record<string, number>;
}

/** One product in the "recently created" feed. */
export interface EventCreatedRow {
  id: string;
  title: string;
  createdAt: string;
  /** Often OUTSIDE the attendance window — see `attendees`. */
  start: string;
  eventType: string;
  createdBy: string;
  creatorUsername: string;
  creatorName: string;
  /**
   * NULL means this product's `start` fell outside the attendance window, so it
   * was never scanned. That is emphatically not "nobody came" — 45% of products
   * created in the window start beyond it. Render null as "—", never as 0.
   */
  attendees: number | null;
}

/**
 * Cached — `adminEventCreatedStats/current`.
 *
 * **A separate document from `EventsResponse`, because it is a separate
 * population.** That one is `events` queried by `start`; this one by
 * `createdAt`. Measured over the same 100 days (Sep 2026): 761 products start
 * in the window, 1,146 were created in it, and **516 (45%) of the second set
 * are absent from the first** because they are scheduled beyond it. Aggregating
 * the existing rows would have silently dropped nearly half the board.
 */
export interface EventsCreatedResponse {
  creators: EventCreatorRow[];
  recent: EventCreatedRow[];
  /** Oldest-first, seeded across the window — an empty day is a real zero. */
  createdByDay: CountDay[];
  windowFrom: string | null;
  productsCreated: number;
  productsScanned: number;
  /** How many carry a real attendee count — typically ~55% of the window. */
  productsScored: number;
  recentTruncated: boolean;
  computedAt: string | null;
  sync: SyncMeta;
}

export type PaymentStatusFilter = "succeeded" | "failed";

/**
 * One individual Stripe payment.
 *
 * `buyer.phoneNumber` is populated by the backend on FAILED rows only — that is
 * where a phone number has a job to do (look the buyer up and call them). A
 * succeeded payment needs no follow-up, so its buyer's number never leaves the
 * backend rather than being sent and hidden here.
 */
export interface PaymentRow {
  chargeId: string;
  paymentIntentId: string;
  createdAt: string;
  amountCents: number;
  amountRefundedCents: number;
  currency: string;
  status: PaymentStatusFilter;
  /** Stripe's decline reason. Failed rows only. */
  failureCode: string | null;
  failureMessage: string | null;
  buyer: {
    uid: string;
    fullName: string;
    username: string;
    phoneNumber: string | null;
  };
  /** Null when the payment is not a ticket purchase. */
  event: { id: string; title: string } | null;
  ticket: { id: string; label: string; count: number } | null;
}

export interface PaymentsResponse {
  payments: PaymentRow[];
  status: PaymentStatusFilter;
  range: { key: DateRangeKey; from: string; to: string };
  /**
   * Totals for the CURRENT PAGE only. Stripe's search API returns a cursor page
   * with no total count, so a range-wide figure would have to be invented — and
   * an invented total on an admin dashboard is a number someone quotes.
   */
  pageTotals: {
    count: number;
    grossCents: number;
    refundedCents: number;
    ticketCount: number;
  };
  hasMore: boolean;
  nextCursor: string | null;
  fetchedAt: string;
}

export interface EngagementDay {
  date: string;
  /** Firebase Auth account creations on this UTC day. Always a real number. */
  signups: number;
  /**
   * The subset of `signups` who have since finished Markit's onboarding flow —
   * "creator sign ups". Always ≤ `signups`, on the same UTC day and the same
   * Firebase Auth timestamp. Always a real number; 0 is a genuine zero.
   */
  creatorSignups: number;
  /**
   * Null means the day falls outside the rollup's event window — genuinely
   * unknown, and rendered as a gap. A 0 inside the window is a real zero.
   */
  eventsAttended: number | null;
  eventsWith20: number | null;
}

/**
 * Cached — the nightly rollup's `adminEngagementStats/current`.
 *
 * Unlike `PlatformStatsResponse` this carries no `missingDays`: the whole
 * window is rebuilt from source every run, so a day with no activity is a
 * genuine zero rather than a day nobody processed.
 */
export interface EngagementStatsResponse {
  range: { key: DateRangeKey; from: string; to: string; days: number };
  days: EngagementDay[];
  totals: {
    /** Every Firebase Auth account — the signup series' own cross-check. */
    totalAccounts: number;
    /**
     * Every account meeting both creator conditions — the creator series'
     * cross-check, and the all-time counterpart to `creatorSignups`.
     */
    totalCreatorAccounts: number;
    /**
     * Firestore records matching the creator filter before intersecting with
     * Auth. Slightly higher than `totalCreatorAccounts`; the difference is
     * `creatorRecordsWithoutAuth`.
     */
    creatorRecords: number;
    /**
     * Finished-onboarding records whose Firebase Auth account no longer exists,
     * so they carry no creation date and sit in no daily bucket.
     */
    creatorRecordsWithoutAuth: number;
    /**
     * Records matching the onboarding flag ALONE, before the email requirement
     * — what "creator sign ups" counted before Sep 2026.
     */
    creatorRecordsFlagOnly: number;
    /**
     * Records excluded for having no email address. The size of the deliberate
     * undercount, mostly subscribers plus some legacy phone-first creators.
     *
     * 0 means the rollup predates this field, not that nothing was excluded.
     */
    creatorRecordsWithoutEmail: number;
    /**
     * Every Firestore `users` document — AUDIENCE REACH, not a signup figure.
     * Most of these are imported contacts and event attendees.
     *
     * 0 means the nightly count failed or predates this field, never an empty
     * collection, so it must render as "—" rather than as a measurement.
     */
    totalUserRecords: number;
    /**
     * When `totalUserRecords` was last actually recomputed — this scan runs
     * WEEKLY, not nightly like the rest of this page. Show this timestamp on
     * that tile specifically; `computedAt` below reflects the rest of the
     * document and would overstate this figure's freshness.
     */
    totalUserRecordsComputedAt: string | null;
    eventsScanned: number;
    ticketsScanned: number;
  } | null;
  /** Null when the nightly job has never written the document. */
  computedAt: string | null;
  eventsWindowFrom: string | null;
  attendeeThreshold: number;
  sync: { lastRunFinishedAt: string | null; status: string | null };
}

export interface LeaderboardEntry {
  uid: string;
  fullName: string;
  username: string;
  profilePicURL: string;
  /**
   * Current subscription state. Anything other than ACTIVE/TRIAL means this
   * creator earned Markit this money and has since churned — real revenue, but
   * not a current customer, and this board sits above a table of current
   * customers. Absent on responses from before Sep 2026.
   */
  subscriptionState?: string;
  /**
   * Scoped to the response's `window`. Subscription includes SMS overflow —
   * both are the creator's subscription bill, and the card stacks them as one.
   */
  totalRevenueCents: number;
  subscriptionRevenueCents: number;
  ticketingFeeRevenueCents: number;
  otherFeeRevenueCents: number;
  /**
   * Stripe's cut on each of the three segments above, grouped exactly as the
   * revenue is, so the net stack's segments add up to its own total.
   *
   * Note how differently the two streams net out, and that it is real: Markit
   * is merchant of record on the WHOLE ticket price, so Stripe charges its
   * percentage on the full charge while Markit keeps only its application fee
   * — measured 2026-09-05, ticketing retained 68% against subscription's 96%.
   * A creator whose revenue is mostly ticketing therefore moves DOWN this
   * board on the net basis, which is why the endpoint re-sorts rather than
   * letting the browser re-label a gross ordering.
   */
  subscriptionStripeFeeCents: number;
  ticketingStripeFeeCents: number;
  otherStripeFeeCents: number;
  stripeFeeCents: number;
  netRevenueCents: number;
  /**
   * Always lifetime, whatever the window — the counterpart to
   * `lifetimeMessagesSent` on the volume board. On a `lifetime` response it is
   * the same number as `totalRevenueCents`; on a trailing one it says what this
   * creator has ever been worth, next to what they were worth this month.
   */
  lifetimeRevenueCents: number;
  /** Its net counterpart, for when the board is on the net basis. */
  lifetimeNetRevenueCents: number;
}

export interface LeaderboardResponse {
  /**
   * Echoed by the endpoint. Defaults to `lifetime` — unlike the volume board's
   * `30d` — because that is the only answer this endpoint gave before Sep 2026.
   */
  window: RevenueWindow;
  /**
   * Echoed too, and for a sharper reason than `window`: the basis changes the
   * ORDER, not just the column. The card must name the basis it was actually
   * ranked on rather than the one that was asked for.
   */
  basis: RevenueBasis;
  customers: LeaderboardEntry[];
  sync: SyncMeta;
}

/**
 * Two refund fields, and they mean opposite things. See the backend README's
 * "Refunds: two kinds" table.
 *
 *  - `refundedFeeCents` — application fee given back on a ticket/product sale.
 *    Rare, because a creator refunding a ticket absorbs it and Markit keeps its
 *    fee. GROSS: already subtracted inside `totalRevenueCents`.
 *  - `subscriptionRefundCents` — a creator's subscription payment refunded.
 *    This is Markit's own money. ALREADY netted out of
 *    `subscriptionRevenueCents`/`overflowRevenueCents`, so never subtract it
 *    from them — it is here to be displayed, not applied.
 */
export interface RevenueTotals {
  subscriptionRevenueCents: number;
  overflowRevenueCents: number;
  ticketingFeeRevenueCents: number;
  otherFeeRevenueCents: number;
  refundedFeeCents: number;
  /** Reporting only — already netted out. Absent on pre-Sep-2026 documents. */
  subscriptionRefundCents?: number;
  /**
   * Stripe's own processing cut over the same window, held GROSS — a third
   * kind of thing again from the two refund fields above, and the difference
   * matters. A refund is money Markit took and gave back; this is money that
   * never reached Markit at all. `totalRevenueCents` does not subtract it and
   * never has; `netRevenueCents` is the figure that does.
   *
   * All six are absent on a rollup document written before Sep 2026.
   */
  subscriptionStripeFeeCents?: number;
  overflowStripeFeeCents?: number;
  ticketingStripeFeeCents?: number;
  otherStripeFeeCents?: number;
  stripeFeeCents?: number;
  netRevenueCents?: number;
  totalRevenueCents: number;
  messagesSent: number;
  spendCents: number;
}

export interface CustomerMonthStats {
  month: string;
  subscriptionRevenueCents: number;
  overflowRevenueCents: number;
  ticketingFeeRevenueCents: number;
  otherFeeRevenueCents: number;
  refundedFeeCents: number;
  /** Reporting only — already netted out. Absent on pre-Sep-2026 documents. */
  subscriptionRefundCents?: number;
  /**
   * Stripe's cut, grouped to match the three segments this card stacks —
   * subscription and overflow folded together, as their revenue already is.
   * 0 on a month predating the fee capture; the card states its coverage.
   */
  subscriptionStripeFeeCents?: number;
  ticketingStripeFeeCents?: number;
  otherStripeFeeCents?: number;
  messagesSent: number;
  spendCents: number;
}

export interface CustomerStatsResponse {
  /** null when the rollup has never written this customer. */
  stats: {
    uid: string;
    lifetime: RevenueTotals;
    trailing30d: RevenueTotals;
    trailing90d: RevenueTotals;
    messagingSource: MessagingSource;
    firstRevenueAt: string;
    lastSyncedAt: string;
  } | null;
  months: CustomerMonthStats[];
  sync: SyncMeta;
}

export interface SyncStateResponse {
  state: {
    lastRunStartedAt: string;
    lastRunFinishedAt: string;
    status: "ok" | "partial" | "failed" | null;
    processedThroughDate: string;
    customersProcessed: number;
    errors: string[];
  } | null;
}

export type DmMessageSource = "fan" | "markit" | "creator";

export type DmFlagType =
  | "SEND_FAILURE"
  | "DUPLICATE_SEND"
  | "REPROMPT_LOOP"
  | "BOT_AFTER_CREATOR";

/** One stored Instagram DM thread, from `/engagement/instagram/dms/threads`. */
export interface DmThreadRow {
  igUserId: string;
  igUsername?: string;
  lastMessageText: string;
  lastMessageAt: string;
  lastMessageSource: DmMessageSource;
  flagCount: number;
}

export interface DmThreadsResponse {
  threads: DmThreadRow[];
  nextCursor: string | null;
}

export interface DmMessageRow {
  mid: string;
  source: DmMessageSource;
  text: string;
  sentAt: string;
  status?: "sent" | "failed";
  errorCode?: number;
  errorSubcode?: number;
  attachmentType?: string;
  storyReplyId?: string;
  replyToMid?: string;
  postbackTitle?: string;
  action?: string;
  automationId?: string;
  triggerId?: string;
  commentId?: string;
  commentText?: string;
}

export interface DmMessagesResponse {
  /** Newest first. */
  messages: DmMessageRow[];
  nextCursor: string | null;
}

export interface DmFlagRow {
  id: string;
  type: DmFlagType;
  creatorUid: string;
  igAccountId: string;
  creatorIgUsername?: string;
  fanIgUserId?: string;
  fanIgUsername?: string;
  commentId?: string;
  messageMid?: string;
  details: string;
  attempts?: number;
  final?: boolean;
  recovered?: boolean;
  createdAt: string;
}

/** When an account last got a stored DM. Only accounts with at least one thread are listed. */
export interface DmAccountActivityRow {
  uid: string;
  igAccountId: string;
  lastMessageAt: string;
  lastMessageSource: DmMessageSource;
}

export interface DmAccountActivityResponse {
  accounts: DmAccountActivityRow[];
  fetchedAt: string;
}

export interface DmFlagsResponse {
  flags: DmFlagRow[];
  nextCursor: string | null;
}
